import { env } from "@/lib/env";
import { useAuthStore, isRefreshable } from "@/auth/store";

// Single in-flight refresh promise so concurrent 401s don't fire N
// refresh requests. Subsequent callers await the same rotation.
let refreshInFlight: Promise<boolean> | null = null;

async function performRefresh(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const { refreshToken } = useAuthStore.getState();
      if (!refreshToken) return false;
      const res = await fetch(`${env.dashApiUrl}/auth/refresh`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return false;
      const body = await res.json();
      useAuthStore.getState().setSession(body);
      return true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

// Backend's actual envelope (set in app.ts setErrorHandler):
//   { error: { code, message, details?, request_id } }
// Older / external surfaces sometimes return flatter shapes ({ error: "code",
// message: "..." }) so the constructor accepts both.
export type ApiErrorBody =
  | { error: { code?: string; message?: string; details?: unknown; request_id?: string } }
  | { error?: string; message?: string; details?: unknown };

export class ApiError extends Error {
  status: number;
  code: string;
  body: ApiErrorBody;
  constructor(status: number, body: ApiErrorBody) {
    const inner =
      body && typeof (body as { error?: unknown }).error === "object"
        ? ((body as { error: { code?: string; message?: string; details?: unknown } }).error)
        : null;
    const flatErr = typeof (body as { error?: unknown })?.error === "string"
      ? (body as { error: string }).error
      : undefined;
    const flatMsg = typeof (body as { message?: unknown })?.message === "string"
      ? (body as { message: string }).message
      : undefined;
    const msg = inner?.message ?? flatMsg ?? flatErr ?? `HTTP ${status}`;
    super(msg);
    this.status = status;
    this.code = inner?.code ?? flatErr ?? "unknown";
    this.body = body;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
  // Set true for endpoints that don't need auth (login, callback, refresh).
  skipAuth?: boolean;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(path.startsWith("http") ? path : `${env.dashApiUrl}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function rawRequest<T>(path: string, opts: RequestOptions = {}, attempt = 0): Promise<T> {
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = { accept: "application/json" };
  if (opts.body !== undefined) headers["content-type"] = "application/json";

  if (!opts.skipAuth) {
    const token = useAuthStore.getState().accessToken;
    if (token) headers["authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401 && !opts.skipAuth && attempt === 0 && isRefreshable()) {
    const refreshed = await performRefresh();
    if (refreshed) return rawRequest<T>(path, opts, attempt + 1);
    // Refresh failed — kick to login. Caller may already be on /login;
    // letting the caller surface the 401 + clearing state is enough.
    useAuthStore.getState().clear();
  }

  if (res.status === 204) return undefined as T;

  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = { error: "invalid_response", message: text }; }
  }

  if (!res.ok) {
    throw new ApiError(res.status, (parsed as ApiErrorBody) ?? { error: `http_${res.status}` });
  }
  return parsed as T;
}

export const api = {
  get:    <T>(path: string, opts: Omit<RequestOptions, "method" | "body"> = {})        => rawRequest<T>(path, { ...opts, method: "GET" }),
  post:   <T>(path: string, body?: unknown, opts: Omit<RequestOptions, "method"> = {}) => rawRequest<T>(path, { ...opts, method: "POST", body }),
  patch:  <T>(path: string, body?: unknown, opts: Omit<RequestOptions, "method"> = {}) => rawRequest<T>(path, { ...opts, method: "PATCH", body }),
  delete: <T>(path: string, opts: Omit<RequestOptions, "method" | "body"> = {})        => rawRequest<T>(path, { ...opts, method: "DELETE" }),
};
